import { NgModule, LOCALE_ID } from '@angular/core';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CommonModule, DatePipe, JsonPipe, registerLocaleData } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from './material.module';
import { HttpClientModule } from '@angular/common/http';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingComponent } from './components/loading/loading.component';
import { ClipboardModule } from 'ngx-clipboard';
import { ActionMenuComponent } from './components/action-menu/action-menu.component';
import { ActionMenuItemComponent } from './components/action-menu/action-menu-item/action-menu-item.component';
// import { SatDatepickerModule, SatNativeDateModule } from 'saturn-datepicker-ext';
import br from '@angular/common/locales/pt';
import es from '@angular/common/locales/es';
import { CustomMatPaginatorIntl } from './components/pagination/custom-paginator';
import { CustomDropdownBottomComponent } from './components/custom-dropdown/bottom/custom-dropdown-bottom.component';
import { CustomDropdownComponent } from './components/custom-dropdown/custom-dropdown.component';
import { DynamicFormComponent } from './components/fields-form/dynamic-form/dynamic-form.component';
import { FileComponent } from './components/fields-form/file/file.component';
import { TextareaComponent } from './components/fields-form/textarea/textarea.component';
import { MultiselectComponent } from './components/fields-form/multiselect/multiselect.component'
import { ButtonComponent } from './components/fields-form/button/button.component';
import { SelectComponent } from './components/fields-form/select/select.component';
import { DateComponent } from './components/fields-form/date/date.component';
import { RadiobuttonComponent } from './components/fields-form/radiobutton/radiobutton.component';
import { CheckboxComponent } from './components/fields-form/checkbox/checkbox.component';
import { TooltipComponent } from './components/fields-form/tooltip/tooltip.component';
import { DynamicFieldDirective } from './components/fields-form/dynamic-field/dynamic-field.directive';
import { TwoDigitDecimaNumberDirective } from './directives/two-digit-decimal';
import { ModalAlertComponent } from './components/modal-alert/modal-alert.component';
import { UserService } from './services/user.service';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { AutoSizeInputModule } from 'ngx-autosize-input';
import { MoneyDirective } from './directives/money';
import { CustomDropdownMerchantsBottomComponent } from './components/custom-dropdown-merchants/bottom/custom-dropdown-merchants-bottom.component';
import { CustomDropdownMerchantsComponent } from './components/custom-dropdown-merchants/custom-dropdown-merchants.component';
import { TrimDirective } from './directives/trim.directive';
import { InputComponent } from './components/fields-form/input/input.component';
import { TimeComponent } from './components/fields-form/time/time.component';
import { RegexNameServiceDirective } from './directives/regex-name-service.directive';
import { AutocompleteComponent } from './components/fields-form/autocomplete/autocomplete.component';
import { FullCalendarModule } from '@fullcalendar/angular'; // must go before plugins
import { UrlService } from './services/url.service';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { HighlightPipe } from './pipes/highlight.pipe';
import { XmlFormatterComponent } from './components/xml-formatter/xml-formatter.component';
import { ModalConfirmReportComponent } from './components/modal-confirm-report/modal-confirm-report.component';
import { ReportTypeSelectorModalComponent } from './components/report-type-selector-modal/report-type-selector-modal.component';
import { OptionalLabelDirective } from './directives/optional-label.directive';
import { UpdateOptionalLabelDirective } from './directives/update-optional-label';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PrettyJsonPipe } from './pipes/json.pipe';
import { NguCarousel, NguTileComponent, NguCarouselDefDirective, NguCarouselNextDirective, NguCarouselPrevDirective, NguItemComponent } from '@ngu/carousel';
import { ScheduleComponent } from './components/schedule/schedule.component';
import { InfoDataBankModalComponent } from '../layout/components/select-merchants/modals/info-data-bank-modal.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { QuillModule } from 'ngx-quill';
import { SchedulePartnerComponent } from './components/schedule-partner/schedule-partner.component';
import { NumberFormatPipe } from './pipes/number.pipe';
import { CpfCnpjPipe } from './pipes/cnpjCpfPipe';
import { CustomReuseStrategy } from './utils/custom-reuse-estrategy';
import { FilterServicePipe } from '../contexts/services/pages/list/pipes/filter-service.pipe';
import { FilterConnectionsPipe } from '../contexts/services/pages/list/pipes/filter-connections.pipe';
import { FilterAuthorizationsPipe } from '../contexts/services/pages/list/pipes/filter-authorizations.pipe';
import { LayoutFabButtonComponent } from '../layout/components/fab-button/fab-button.component';
import { LetterService } from './services/letter.service';


// FullCalendarModule.registerPlugins([ // register FullCalendar plugins
//   dayGridPlugin,
//   interactionPlugin
// ]);

export function localeServiceFactory(translate: TranslateService) {

  const browserCultureLang = translate.getBrowserCultureLang().toLocaleLowerCase();
  registerLocaleData(br, 'pt-BR');
  registerLocaleData(es);
  return browserCultureLang;
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    HttpClientModule,
    FlexLayoutModule,
    MaterialModule,
    TranslateModule,
    HttpClientModule,
    ClipboardModule,
    FormsModule,
    ReactiveFormsModule,
    AutoSizeInputModule,
    FullCalendarModule,
    NgxMatTimepickerModule,
    DragDropModule,
    NguCarousel,
    NguTileComponent,
    NguCarousel,
    NguCarouselDefDirective,
    NguCarouselNextDirective,
    NguCarouselPrevDirective,
    NguItemComponent,
    NgxMaskDirective,
    NgxMaskPipe,
    QuillModule.forRoot()
  ],
  declarations: [
    LocalizedDatePipe,
    PrettyJsonPipe,
    HighlightPipe,
    NumberFormatPipe,
    CpfCnpjPipe,
    ClickOutsideDirective,
    TwoDigitDecimaNumberDirective,
    MoneyDirective,
    CustomDropdownComponent,
    CustomDropdownBottomComponent,
    CustomDropdownMerchantsComponent,
    CustomDropdownMerchantsBottomComponent,
    ActionMenuComponent,
    ActionMenuItemComponent,
    LoadingComponent,
    ModalAlertComponent,
    ModalConfirmReportComponent,
    ReportTypeSelectorModalComponent,
    InputComponent,
    TextareaComponent,
    MultiselectComponent,
    ButtonComponent,
    SelectComponent,
    DateComponent,
    RadiobuttonComponent,
    CheckboxComponent,
    TooltipComponent,
    FileComponent,
    TimeComponent,
    DynamicFieldDirective,
    DynamicFormComponent,
    TrimDirective,
    RegexNameServiceDirective,
    AutocompleteComponent,
    XmlFormatterComponent,
    OptionalLabelDirective,
    UpdateOptionalLabelDirective,
    ScheduleComponent,
    SchedulePartnerComponent,
    InfoDataBankModalComponent,
    FilterServicePipe,
    FilterConnectionsPipe,
    FilterAuthorizationsPipe,
    LayoutFabButtonComponent,
  ],
  providers: [
    UserService,
    LetterService,
    UrlService,
    DatePipe,
    {
      provide: LOCALE_ID,
      useFactory: localeServiceFactory,
      deps: [TranslateService]
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl
    },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    provideNgxMask()],
  exports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HttpClientModule,
    FlexLayoutModule,
    MaterialModule,
    TranslateModule,
    ClipboardModule,
    ReactiveFormsModule,
    FullCalendarModule,
    NgxMatTimepickerModule,
    DragDropModule,
    ClickOutsideDirective,
    TwoDigitDecimaNumberDirective,
    MoneyDirective,
    CustomDropdownComponent,
    CustomDropdownBottomComponent,
    CustomDropdownMerchantsComponent,
    CustomDropdownMerchantsBottomComponent,
    ActionMenuComponent,
    ActionMenuItemComponent,
    LoadingComponent,
    ModalAlertComponent,
    ModalConfirmReportComponent,
    ReportTypeSelectorModalComponent,
    InputComponent,
    TextareaComponent,
    MultiselectComponent,
    ButtonComponent,
    SelectComponent,
    DateComponent,
    RadiobuttonComponent,
    CheckboxComponent,
    TimeComponent,
    TooltipComponent,
    FileComponent,
    DynamicFieldDirective,
    DynamicFormComponent,
    LocalizedDatePipe,
    PrettyJsonPipe,
    HighlightPipe,
    NumberFormatPipe,
    CpfCnpjPipe,
    AutoSizeInputModule,
    TrimDirective,
    RegexNameServiceDirective,
    AutocompleteComponent,
    XmlFormatterComponent,
    OptionalLabelDirective,
    UpdateOptionalLabelDirective,
    NguCarousel,
    NguTileComponent,
    NguCarousel,
    NguCarouselDefDirective,
    NguCarouselNextDirective,
    NguCarouselPrevDirective,
    NguItemComponent,
    ScheduleComponent,
    SchedulePartnerComponent,
    InfoDataBankModalComponent,
    NgxMaskDirective,
    NgxMaskPipe,
    QuillModule,
    FilterServicePipe,
    FilterConnectionsPipe,
    FilterAuthorizationsPipe,
    LayoutFabButtonComponent,

  ],
  // entryComponents: [
  //   InputComponent,
  //   TextareaComponent,
  //   MultiselectComponent,
  //   ButtonComponent,
  //   SelectComponent,
  //   DateComponent,
  //   RadiobuttonComponent,
  //   CheckboxComponent,
  //   TooltipComponent,
  //   TimeComponent,
  //   AutocompleteComponent
  // ]
})
export class CoreModule { }
